import { useFormikContext, ErrorMessage } from "formik";
import { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { SimpleClusterForm } from "@/types";
import { BinIcon } from "@/components/Icons/BinIcon";
import { Separator } from "@/components/ui/separator";
import { required } from "@/components/forms/validation";
import { SelectInputField, TextField } from "@/components/forms/fields";
import { cn, isValidIPorSubnet, validatePort, isValidName } from "@/lib/utils";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { SimpleTooltip } from "@/components/Common/Tooltip/SimpleTooltip";
import { RequiredIndicator } from "@/components/Common/RequiredIndicator/RequiredIndicator";

const PROTOCOL_OPTIONS = [
  { value: "tcp", label: "TCP" },
  { value: "udp", label: "UDP" }
];

export const NetworkServices = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext<SimpleClusterForm>();

  useEffect(() => {
    values.networkServices?.forEach((service, index) => {
      if (Number(service.port) === 22 && service.name !== "ssh") {
        setFieldValue(`networkServices.${index}.name`, "ssh");
      }
    });
  }, [values.networkServices, setFieldValue]);

  const handleAddNetworkService = () => {
    setFieldValue("networkServices", [
      ...(values.networkServices || []),
      { id: uuidv4(), name: "", whitelistIps: ["0.0.0.0/0"], protocol: "tcp" }
    ]);
  };

  const handleRemoveWhitelistIp = (serviceId: string, ipIndex: number) => {
    const updatedServices = values.networkServices?.map((service) => {
      if (service.id === serviceId) {
        return {
          ...service,
          whitelistIps: service.whitelistIps.filter((_, index) => index !== ipIndex)
        };
      }
      return service;
    });
    setFieldValue("networkServices", updatedServices);
  };

  const handleAddWhitelistIp = (serviceId: string) => {
    const updatedServices = values.networkServices?.map((service) => {
      if (service.id === serviceId) {
        return {
          ...service,
          whitelistIps: [...(service.whitelistIps || []), ""]
        };
      }
      return service;
    });
    setFieldValue("networkServices", updatedServices);
  };

  const handleRemoveService = (serviceId: string) => {
    const updatedServices = values.networkServices?.filter((service) => service.id !== serviceId);
    setFieldValue("networkServices", updatedServices);
  };

  const handleUniqueNameValidate = (serviceName: string) => {
    const filteredKeys = (values.networkServices || []).filter(
      ({ name }) => serviceName && name.toLowerCase() === serviceName.toLowerCase()
    );
    return filteredKeys.length > 1 ? "Name must be unique" : undefined;
  };

  const handleUniquePortProtocolValidate = (
    servicePort: number | string,
    serviceProtocol?: string
  ) => {
    const portNum = Number(servicePort);
    if (!portNum || !serviceProtocol) return undefined;
    const filteredPairs = (values.networkServices || []).filter(({ port, protocol }) => {
      const candidatePort = Number(port);
      const candidateProtocol = (protocol || "").toLowerCase();
      return candidatePort === portNum && candidateProtocol === serviceProtocol.toLowerCase();
    });
    return filteredPairs.length > 1 ? "Port and protocol combination must be unique" : undefined;
  };

  return (
    <div className="flex flex-col gap-6">
      {values.networkServices?.map((service, index) => (
        <div
          key={service.id}
          className="flex flex-col gap-6 rounded-[8px] border border-gray-dark-200 bg-background"
        >
          <div className="flex flex-col justify-between px-6 pt-6 lg:flex-row">
            <div className="flex flex-col gap-1">
              <div className="label-medium text-foreground">Add Network Service</div>
              <div className="subtext-small text-gray-light-1700">
                Port forwarding rules that allow external traffic to reach your container or virtual
                machine
              </div>
            </div>
            <Button
              variant="outline"
              className="label-small my-auto gap-2 text-foreground max-lg:mt-4"
              onClick={() => handleRemoveService(service.id)}
            >
              <BinIcon className="h-4 w-4" /> Remove
            </Button>
          </div>
          <Separator />
          <div className="flex flex-col gap-4 px-6 lg:flex-row">
            <div className="flex flex-1 flex-col gap-2">
              <div className="label-small flex items-center text-foreground">
                Name <RequiredIndicator />
                <SimpleTooltip
                  id="network-service-name-tooltip"
                  content="A human-readable identifier for the port forwarding rule."
                >
                  <InfoCircledIcon className="ml-1 h-4 w-4 text-gray-light-1700" />
                </SimpleTooltip>
              </div>
              <TextField
                name={`networkServices.${index}.name`}
                placeholder="Name"
                disabled={Number(service.port) === 22}
                validate={(value) => {
                  return required(value) || isValidName(value) || handleUniqueNameValidate(value);
                }}
                className={cn(
                  "max-h-10 bg-gray-dark-600 text-foreground placeholder:text-gray-light-1700 focus:border-gray-dark-200 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                  // @ts-expect-error TS2339 will appear here, but we know what we’re doing
                  errors.networkServices?.[index]?.name && touched.networkServices?.[index]?.name
                    ? "border-red-dark-300"
                    : "border-border"
                )}
              />
              <ErrorMessage
                name={`networkServices.${index}.name`}
                component="div"
                className="subtext-x-small -mt-5 italic text-red-light-200"
              />
            </div>
            <div className="flex flex-1 flex-col gap-2">
              <div className="label-small flex items-center text-foreground">
                Port <RequiredIndicator />
                <SimpleTooltip
                  id="network-service-port-tooltip"
                  content="The network port that will be opened to receive incoming traffic."
                >
                  <InfoCircledIcon className="ml-1 h-4 w-4 text-gray-light-1700" />
                </SimpleTooltip>
              </div>
              <TextField
                name={`networkServices.${index}.port`}
                placeholder="Port"
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-") {
                    e.preventDefault();
                  }
                }}
                validate={(value) => {
                  return (
                    required(value) ||
                    validatePort(value, true) ||
                    handleUniquePortProtocolValidate(
                      value,
                      values.networkServices?.[index]?.protocol
                    )
                  );
                }}
                disabledOnWheel
                className={cn(
                  "max-h-10 bg-gray-dark-600 text-foreground placeholder:text-gray-light-1700 focus:border-gray-dark-200 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                  // @ts-expect-error TS2339 will appear here, but we know what we’re doing
                  errors.networkServices?.[index]?.port && touched.networkServices?.[index]?.port
                    ? "border-red-dark-300"
                    : "border-border"
                )}
              />
              <ErrorMessage
                name={`networkServices.${index}.port`}
                component="div"
                className="subtext-x-small -mt-5 italic text-red-light-200"
              />
            </div>
          </div>
          <div className="-mt-5 flex w-full flex-col gap-2 px-6">
            <div className="label-small flex items-center text-foreground">
              Protocol <RequiredIndicator />
              <SimpleTooltip
                id="network-service-protocol-tooltip"
                content="The network protocol (TCP or UDP) used to handle incoming traffic."
              >
                <InfoCircledIcon className="ml-1 h-4 w-4 text-gray-light-1700" />
              </SimpleTooltip>
            </div>
            <SelectInputField
              name={`networkServices.${index}.protocol`}
              options={PROTOCOL_OPTIONS}
              validate={(value) =>
                required(value) ||
                handleUniquePortProtocolValidate(values.networkServices?.[index]?.port ?? "", value)
              }
              className="w-full border-gray-dark-200 bg-gray-dark-600"
            />
          </div>
          <div className="-mt-5 flex w-full flex-col gap-2 px-6">
            <div className="label-small flex items-center text-foreground">
              Whitelist IPs <RequiredIndicator />
              <SimpleTooltip
                id="network-service-whitelist-ips-tooltip"
                content="A CIDR-formatted subnet or IP range. Only IPs matching this CIDR block are permitted; all others are denied access."
              >
                <InfoCircledIcon className="ml-1 h-4 w-4 text-gray-light-1700" />
              </SimpleTooltip>
            </div>
            {service.whitelistIps.map((_ip, ipIndex) => (
              <div className="flex flex-col gap-0.5" key={`whitelistip-${service.id}-${ipIndex}`}>
                <div className="flex flex-row gap-2">
                  <TextField
                    name={`networkServices.${index}.whitelistIps.${ipIndex}`}
                    placeholder="IPv4 subnet"
                    type="string"
                    validate={(value) => {
                      return (
                        required(value) ||
                        (isValidIPorSubnet(value) ? undefined : "Invalid IP address format")
                      );
                    }}
                    className={cn(
                      "bg-gray-dark-600 text-foreground placeholder:text-gray-light-1700 focus:border-gray-dark-200 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                      // @ts-expect-error TS2339 will appear here, but we know what we’re doing
                      errors.networkServices?.[index]?.whitelistIps?.[ipIndex] &&
                        // @ts-expect-error TS2339 will appear here, but we know what we’re doing
                        touched.networkServices?.[index]?.whitelistIps?.[ipIndex]
                        ? "border-red-dark-300"
                        : "border-border"
                    )}
                  />
                  {ipIndex === service.whitelistIps.length - 1 ? (
                    <Button
                      type="button"
                      variant={"outline"}
                      onClick={() => handleAddWhitelistIp(service.id)}
                      className="rounded px-3 py-2"
                    >
                      <PlusIcon className="h-[16px] w-[16px] text-foreground" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant={"outline"}
                      onClick={() => handleRemoveWhitelistIp(service.id, ipIndex)}
                      className="bg-red-600 hover:bg-red-500 rounded px-3 py-2"
                    >
                      <BinIcon className="h-[16px] w-[16px] text-gray-dark-1000" />
                    </Button>
                  )}
                </div>

                <ErrorMessage
                  name={`networkServices.${index}.whitelistIps.${ipIndex}`}
                  component="div"
                  className="subtext-x-small -mt-5 italic text-red-light-200"
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="relative flex h-[100px] flex-row items-center justify-between rounded-[8px] border border-gray-dark-200 bg-background px-6">
        <div className="label-medium text-foreground">Add Network Service</div>
        <Button
          variant="outline"
          className="label-small z-[2] gap-2 text-foreground"
          onClick={handleAddNetworkService}
          disabled={(values.networkServices?.length || 0) >= 10}
        >
          <PlusIcon className="h-4 w-4" /> Add
        </Button>
        <img
          src="/assets/images/ssh-keys.png"
          className="absolute left-[311px] z-[1] max-lg:hidden hidden dark:block"
          alt=""
        />
      </div>
    </div>
  );
};
